import React from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { formatPoints, formatSignedPoints } from "./formatPoints";

const PointsSummary = ({ program, pointBalance, myPoints, eventHistory = [] }) => {
  const { t } = useTranslation();

  const factor = program.factor_valuation;

  const formatDate = (value) => {
    if (!value) return "-";

    const parsed = new Date(
      typeof value === "string" ? value.replace(" ", "T") : value
    );

    if (Number.isNaN(parsed.getTime())) return "-";

    return parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getRowDate = (row) =>
    row?.date ||
    row?.journal_event_timestamp ||
    row?.created_at ||
    row?.updated_at ||
    row?.expiration ||
    null;

  const getAwarder = (row) => {
    const awarder = `${row?.first_name ?? ""} ${row?.last_name ?? ""}`.trim();
    return awarder || "-";
  };

  const stats = [
    {
      label: t('your_points_balance'),
      value: formatPoints(pointBalance.amount * factor),
      accent: true,
    },
    {
      label: t('points_redeemed'),
      value: formatPoints(pointBalance.redeemedBalance * factor),
    },
    {
      label: t('points_expired'),
      value: formatPoints(pointBalance.expiredBalance * factor),
    },
  ];

  const recentActivityRows = eventHistory.slice(0, 5).map((row, index) => {
    const amount = Number(row?.amount ?? 0);

    return {
      id: row?.journal_event_id ?? `activity-${index}`,
      name: row?.name ?? "-",
      date: formatDate(row?.journal_event_timestamp),
      awarder: getAwarder(row),
      points: formatSignedPoints(amount * factor),
      pointsClass:
        amount > 0
          ? "mp-table__pts--pos"
          : amount < 0
            ? "mp-table__pts--neg"
            : "",
    };
  });

  const summaryRows = [
    ...(myPoints.points_summary ?? []),
    {
      name: t('points_reclaimed'),
      points: (myPoints.points_reclaimed ?? 0) * factor,
      date: null,
    },
  ].map((row, index) => ({
    id: `summary-${index}`,
    name: row?.name ?? "-",
    date: formatDate(getRowDate(row)),
    awarder: "-",
    points: formatPoints(row?.points ?? 0),
    pointsClass: "",
  }));

  const rows = recentActivityRows.length > 0 ? recentActivityRows : summaryRows;
  const tableTitle = recentActivityRows.length > 0 ? t('recent_activity') : t('points_summary');

  return (
    <div className="mp-summary">
      <div className="mp-stats">
        {stats.map((s, i) => (
          <div key={i} className={`mp-stat${s.accent ? ' mp-stat--accent' : ''}`}>
            <span className="mp-stat__value">{s.value}</span>
            <span className="mp-stat__label">{s.label}</span>
          </div>
        ))}
      </div>

      {rows.length > 0 && (
        <div className="mp-table-wrap">
          <h2 className="mp-table-wrap__title">{tableTitle}</h2>
          <table className="mp-table">
            <thead>
              <tr>
                <th>{t('event')}</th>
                <th>{t('date')}</th>
                <th>{t('Awarder')}</th>
                <th className="mp-table__num">{t('points')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.id ?? i}>
                  <td data-label={t('event')}>{row.name}</td>
                  <td className="mp-table__date" data-label={t('date')}>{row.date}</td>
                  <td data-label={t('Awarder')}>{row.awarder}</td>
                  <td className={`mp-table__num ${row.pointsClass}`.trim()} data-label={t('points')}>{row.points}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  myPoints:     state.participant.myPoints,
  pointBalance: state.pointBalance,
  program:      state.program,
});

export default connect(mapStateToProps)(PointsSummary);
